import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Spin, Tree } from '@myua/kit';
import { DataNode } from 'rc-tree/lib/interface';
import * as api from '../../../api';
import { Category, Project } from '../../../api';
import { TM } from '../tokens';
import { ArrowDownIcon, CloseIcon, SearchIcon } from '../icons';
import { Destination } from '../saveToAssets/types';

// Picks a save destination when one can't be derived from the first reference,
// or when the user wants to choose a different one. Projects are the root nodes;
// a project's categories load lazily on expand (the categories endpoint is
// per-project, so projects can't be resolved from one combined list). Typing in
// the filter eager-loads every project's categories so the name search spans the
// whole tree. Only categories are selectable — never a bare project root (a real
// category is required, the same rule the uploader enforces).

const PROJECT_KEY_PREFIX = 'p:';
const projectNodeKey = (id: number) => `${PROJECT_KEY_PREFIX}${id}`;
const isProjectKey = (key: React.Key) =>
  typeof key === 'string' && key.startsWith(PROJECT_KEY_PREFIX);

// Flat category list (each with its `path` of ancestor ids) → nested DataNodes.
function buildCategorySubtree(cats: Category[]): DataNode[] {
  type Node = DataNode & { parentKey: string | null };
  const byKey: Record<string, Node> = {};
  const roots: Node[] = [];
  const nodes: Node[] = cats.map((c) => {
    const node: Node = {
      key: c.id,
      title: c.name,
      parentKey: c.path.length > 0 ? c.path[c.path.length - 1] : null,
      selectable: true,
    };
    byKey[c.id] = node;
    return node;
  });
  nodes.forEach((node) => {
    const parent = node.parentKey != null ? byKey[node.parentKey] : undefined;
    if (parent) {
      parent.children = parent.children || [];
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });
  // Leaf categories must not show an expander or trigger loadData.
  nodes.forEach((n) => {
    if (!n.children) n.isLeaf = true;
  });
  return roots;
}

// Keep nodes whose title matches `q` or that have a matching descendant; when a
// node (or an ancestor) matches, its whole subtree is kept. Also collects the
// keys to auto-expand so matches are revealed.
function filterTree(nodes: DataNode[], q: string): { nodes: DataNode[]; expanded: React.Key[] } {
  const expanded: React.Key[] = [];
  const walk = (list: DataNode[], ancestorMatch: boolean): DataNode[] =>
    list.reduce<DataNode[]>((acc, node) => {
      const selfMatch = String(node.title).toLowerCase().includes(q);
      const keepAll = ancestorMatch || selfMatch;
      const filteredChildren = node.children ? walk(node.children, keepAll) : [];
      const children = keepAll ? node.children ?? [] : filteredChildren;
      if (keepAll || filteredChildren.length) {
        if (children.length) expanded.push(node.key);
        acc.push({ ...node, children });
      }
      return acc;
    }, []);
  return { nodes: walk(nodes, false), expanded };
}

export const CategoryPickerModal: React.FC<{
  open: boolean;
  onPick: (dest: Destination) => void;
  onClose: () => void;
}> = ({ open, onPick, onClose }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [catsByProject, setCatsByProject] = useState<Record<number, Category[]>>({});
  const [busyProjects, setBusyProjects] = useState<Record<number, boolean>>({});
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [filter, setFilter] = useState('');

  // Load the project list when opened; reset transient UI when closed.
  useEffect(() => {
    if (!open) {
      setFilter('');
      setExpandedKeys([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingProjects(true);
    void api.loadProjects().then((res) => {
      if (cancelled) return;
      setProjects(res.success ? res.data.response : []);
      setLoadingProjects(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const loadForProject = useCallback(
    (pid: number): Promise<void> => {
      if (catsByProject[pid] || busyProjects[pid]) return Promise.resolve();
      setBusyProjects((s) => ({ ...s, [pid]: true }));
      return api.loadCategories([pid]).then((res) => {
        setCatsByProject((prev) => ({ ...prev, [pid]: res.success ? res.data.response : [] }));
        setBusyProjects((s) => ({ ...s, [pid]: false }));
      });
    },
    [catsByProject, busyProjects],
  );

  const query = filter.trim().toLowerCase();

  // Filtering needs every project's categories loaded so the search is complete.
  useEffect(() => {
    if (!open || !query) return;
    projects.forEach((p) => {
      if (!catsByProject[p.id] && !busyProjects[p.id]) void loadForProject(p.id);
    });
  }, [open, query, projects, catsByProject, busyProjects, loadForProject]);

  const { catById, ownerByCat } = useMemo(() => {
    const byId: Record<string, Category> = {};
    const owner: Record<string, number> = {};
    Object.entries(catsByProject).forEach(([pid, cats]) => {
      cats.forEach((c) => {
        byId[c.id] = c;
        owner[c.id] = Number(pid);
      });
    });
    return { catById: byId, ownerByCat: owner };
  }, [catsByProject]);

  const baseTree: DataNode[] = useMemo(
    () =>
      projects.map((p) => ({
        key: projectNodeKey(p.id),
        title: p.name,
        selectable: false,
        isLeaf: false,
        children: catsByProject[p.id] ? buildCategorySubtree(catsByProject[p.id]) : undefined,
      })),
    [projects, catsByProject],
  );

  const { treeData, forcedExpanded } = useMemo(() => {
    if (!query) return { treeData: baseTree, forcedExpanded: null as React.Key[] | null };
    const filtered = filterTree(baseTree, query);
    return { treeData: filtered.nodes, forcedExpanded: filtered.expanded };
  }, [baseTree, query]);

  const handleSelect = (keys: React.Key[]) => {
    const key = keys[0];
    if (typeof key !== 'string' || isProjectKey(key)) return;
    const cat = catById[key];
    const project = projects.find((p) => p.id === ownerByCat[key]);
    if (!cat || !project) return;
    onPick({
      projectId: project.id,
      projectName: project.name,
      categoryId: cat.id,
      categoryName: cat.name,
      categoryPath: cat.path,
    });
  };

  if (!open) return null;

  // Portal to the body and sit above the Magic Studio modal (and clear of the
  // history card's hover-opacity, which would otherwise dim a descendant).
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560,
          height: 540,
          background: '#fff',
          borderRadius: 10,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <div
          style={{
            padding: 14,
            borderBottom: `1px solid ${TM.border}`,
            display: 'flex',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: TM.text }}>Choose a category</div>
          <div style={{ flex: 1 }} />
          <div className="al-magic-search" style={{ width: 280 }}>
            <span style={{ color: TM.textFaint, display: 'inline-flex' }}>
              <SearchIcon size={14} />
            </span>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by name…"
              autoFocus
            />
            {filter && (
              <button type="button" onClick={() => setFilter('')} aria-label="Clear filter">
                <CloseIcon size={12} />
              </button>
            )}
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '10px 12px' }}>
          <Spin spinning={loadingProjects}>
            {projects.length === 0 && !loadingProjects ? (
              <div
                style={{ textAlign: 'center', color: TM.textFaint, fontSize: 13, paddingTop: 40 }}
              >
                No projects available.
              </div>
            ) : (
              <Tree
                blockNode
                switcherIcon={<ArrowDownIcon size={10} />}
                treeData={treeData}
                expandedKeys={forcedExpanded ?? expandedKeys}
                selectedKeys={[]}
                loadData={(node) =>
                  isProjectKey(node.key)
                    ? loadForProject(Number(String(node.key).slice(PROJECT_KEY_PREFIX.length)))
                    : Promise.resolve()
                }
                onSelect={handleSelect}
                onExpand={(keys) => setExpandedKeys(keys)}
              />
            )}
          </Spin>
        </div>
      </div>
    </div>,
    document.body,
  );
};
